import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { z } from "zod";

import { type UpdatePersonRequest } from "@ravnar/api-contract";

import { ApiError } from "../../api/client";
import { personQueryOptions, removePerson, rolesAndTeamsQueryOptions, updatePerson } from "../../api/people";
import { useAskAgain } from "../../api/refresh";
import { sessionQueryOptions, useSession } from "../../api/session";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { buttonClassName } from "../../components/ui/button-styles";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { showToast } from "../../lib/toast";
import { PartyDocuments } from "../directory/-components/party-documents";
import { NoAccess } from "./-components/no-access";
import { peopleListSearchSchema } from "./-components/people-list-search";
import { PersonContracts } from "./-components/person-contracts";
import { PersonRecordForm } from "./-components/person-record-form";

/** The list's page and team or role the person was opened from, which Back returns to, and the contract shown. */
const personSearchSchema = peopleListSearchSchema.extend({
	/** "new" opens the drawer that adds a contract; a contract's id opens that contract. */
	contract: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/people/$personId")({
	validateSearch: personSearchSchema,
	loader: async ({ context, params }) => {
		// A member with no access to People & HR is told so by the page; asking the API would only answer 403.
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null || session.member.access.people === "none") {
			return;
		}
		try {
			const [person] = await Promise.all([
				context.queryClient.query({ ...personQueryOptions(params.personId), staleTime: "static" }),
				context.queryClient.query({ ...rolesAndTeamsQueryOptions(), staleTime: "static" }),
			]);
			return person;
		} catch (error) {
			// Somebody removed them, or the id was typed by hand: the router's not-found page says so.
			if (error instanceof ApiError && error.status === 404) {
				notFound({ throw: true });
			}
			throw error;
		}
	},
	head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.name ?? "Person"} · People & HR · Ravnar` }] }),
	staticData: { crumbs: ["People & HR", "Person"] },
	component: PersonPage,
});

interface PersonDetailProps {
	/** Only an editor of People & HR may change the record, save the note, or remove the person. */
	readonly canEdit: boolean;
}

function PersonDetail({ canEdit }: PersonDetailProps) {
	const { personId } = Route.useParams();
	// Everything in the address but the contract shown is the list the person was opened from, which Back and a
	// removal both go back to.
	const { contract, ...list } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { data: person } = useSuspenseQuery(personQueryOptions(personId));
	const { data: rolesAndTeams } = useSuspenseQuery(rolesAndTeamsQueryOptions());
	const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
	const formId = useId();

	// A change to a person touches the list, the roles and teams and the person, which all start with "people", and the
	// audit log, which lives under "settings".
	const refreshPeople = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: ["people"] }),
			queryClient.invalidateQueries({ queryKey: ["settings"] }),
			// A person is projected into the Directory, and the audit entry arrives through a queue: both land
			// after this answer did.
			askAgain(["directory"], ["settings", "audit-log"]),
		]);
	const saveMutation = useMutation({
		mutationFn: (input: UpdatePersonRequest) => updatePerson(personId, input),
		onSuccess: refreshPeople,
	});
	const removeMutation = useMutation({ mutationFn: () => removePerson(personId), onSuccess: refreshPeople });

	const savePerson = async (input: UpdatePersonRequest) => {
		try {
			await saveMutation.mutateAsync(input);
		} catch {
			// The alert under the form shows saveMutation.error.
			return;
		}
		showToast(`Changes to ${input.name} saved.`);
	};

	const removePersonForGood = async () => {
		try {
			await removeMutation.mutateAsync();
		} catch {
			// The dialog's own error prop shows removeMutation.error, which is why it does not close itself.
			return;
		}
		setIsConfirmingRemoval(false);
		// In place of their page, so that going back does not reach it again.
		await navigate({ to: "/people", search: list, replace: true });
		// Kept in the cache, their page would open again as it was, as if they were still here, the next time a link to
		// them is followed.
		queryClient.removeQueries({ queryKey: personQueryOptions(personId).queryKey, exact: true });
		showToast(`${person.name} removed from People & HR.`);
	};

	return (
		<div className="max-w-[880px]">
			<PageHeader
				title={person.name}
				description={`${person.role} · ${person.team}`}
				actions={
					<>
						<Link to="/people" search={list} className={buttonClassName({ size: "md" })}>
							Back to People & HR
						</Link>
						{canEdit ? (
							<Button
								className="text-alert hover:text-alert"
								onClick={() => {
									setIsConfirmingRemoval(true);
								}}
							>
								Remove person
							</Button>
						) : null}
					</>
				}
			/>
			<PersonRecordForm
				key={person.id}
				formId={formId}
				person={person}
				rolesAndTeams={rolesAndTeams}
				canEdit={canEdit}
				onSave={savePerson}
			/>
			{saveMutation.error === null ? null : (
				<p role="alert" className="mt-3.5 text-caption text-alert">
					{saveMutation.error.message}
				</p>
			)}
			{canEdit ? (
				<div className="mt-3.5 flex items-center justify-end gap-2">
					<Button type="submit" form={formId} variant="primary" disabled={saveMutation.isPending}>
						{saveMutation.isPending ? "Saving…" : "Save changes"}
					</Button>
				</div>
			) : null}
			<PersonContracts
				person={person}
				canEdit={canEdit}
				open={contract}
				onOpen={next => {
					void navigate({
						to: "/people/$personId",
						params: { personId },
						// The list the person was opened from stays in the address, for Back.
						search: { ...list, contract: next },
						replace: next === undefined,
					});
				}}
			/>
			<PartyDocuments kind="person" partyId={person.id} name={person.name} />
			<ConfirmDialog
				open={isConfirmingRemoval}
				title={`Remove ${person.name}?`}
				confirmLabel="Remove person"
				busy={removeMutation.isPending}
				error={removeMutation.error?.message}
				onConfirm={() => {
					void removePersonForGood();
				}}
				onClose={() => {
					removeMutation.reset();
					setIsConfirmingRemoval(false);
				}}
			>
				Their record and their note are deleted for good. The audit log keeps that it happened.
			</ConfirmDialog>
		</div>
	);
}

function PersonPage() {
	const { member } = useSession();
	return member.access.people === "none" ? (
		<NoAccess />
	) : (
		<PersonDetail canEdit={member.access.people === "editor"} />
	);
}
